import { Box, Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useContext,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUiI18n } from "../../I18n";
import { GamePageHeader } from "../GamePageHeader/GamePageHeader.tsx";
import { ErrorRegion } from "./ErrorRegion.tsx";
import "./PageScene.css";

export interface PageSceneLink {
	label: string;
	url: string | null;
}

export interface PageSceneAction {
	id: string;
	label: string;
	icon: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	danger?: boolean;
}

const PageSceneActionsContext = createContext<Dispatch<SetStateAction<PageSceneAction[]>> | null>(
	null,
);

export const usePageSceneActions = () => useContext(PageSceneActionsContext);

export const PageScene = ({
	children,
	links,
	title,
	backUrl,
}: {
	children: ReactNode;
	links: PageSceneLink[];
	title: string;
	backUrl?: string;
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useUiI18n();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const [pageActions, setPageActions] = useState<PageSceneAction[]>([]);
	const isCardEditor = location.pathname === "/cards";
	const isWorldDraft = /^\/admin\/worlds\/[^/]+/.test(location.pathname);
	const isWorldWorkspace =
		location.pathname.startsWith("/admin/worlds") || location.pathname.startsWith("/worlds/");
	const sceneArtwork =
		location.pathname === "/analysis"
			? "analysis"
			: location.pathname === "/game-manual"
				? "manual"
				: "workshop";
	const destination =
		backUrl ?? (location.pathname === "/ability-formulas" ? "/cards?tab=gameplay" : "/dashboard");
	const backLabel =
		location.pathname === "/ability-formulas"
			? t("page.cards")
			: location.pathname === "/art-styles/groups"
				? t("dashboard.artStyles")
				: location.pathname === "/editor/design-assets/help"
					? t("dashboard.designAssets")
					: t("common.backToDashboard");

	return (
		<PageSceneActionsContext.Provider value={setPageActions}>
			<div
				className={`page-scene page-scene--${isCardEditor ? "card-editor" : "shared"} page-scene--${sceneArtwork}${isWorldWorkspace ? " page-scene--world-workspace" : ""}`}
			>
				{!isWorldDraft && (
					<GamePageHeader
						title={title}
						backLabel={backLabel}
						moreLabel={t("page.menu")}
						onBack={() => navigate(destination)}
						onMore={(event) => setMenuAnchor(event.currentTarget)}
						moreExpanded={Boolean(menuAnchor)}
						moreControls="page-scene-menu"
					/>
				)}
				<Menu
					id="page-scene-menu"
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={() => setMenuAnchor(null)}
					slotProps={{
						paper: {
							sx: {
								minWidth: 232,
								background: "rgba(8, 15, 28, 0.96)",
								color: "#f4ebcf",
								border: "1px solid rgba(232, 189, 115, 0.48)",
								boxShadow: "0 18px 48px rgba(0, 0, 0, 0.58)",
								backdropFilter: "blur(14px)",
							},
						},
					}}
				>
					{pageActions.length > 0 ? (
						<Box
							sx={{ display: "flex", justifyContent: "space-between", gap: 0.5, px: 1, py: 0.75 }}
						>
							{pageActions.map((action) => (
								<Tooltip key={action.id} title={action.label} arrow>
									<span>
										<IconButton
											data-testid={`page-scene-action-${action.id}`}
											aria-label={action.label}
											disabled={action.disabled}
											color={action.danger ? "error" : "primary"}
											onClick={() => {
												setMenuAnchor(null);
												action.onClick();
											}}
										>
											{action.icon}
										</IconButton>
									</span>
								</Tooltip>
							))}
						</Box>
					) : null}
					{pageActions.length > 0 ? <Divider /> : null}
					{links
						.filter((link) => link.url)
						.map((link) => (
							<MenuItem
								key={link.url}
								selected={link.url === location.pathname}
								onClick={() => {
									setMenuAnchor(null);
									void navigate(link.url!);
								}}
							>
								{link.label}
							</MenuItem>
						))}
				</Menu>
				<div className="page-scene__content">
					<ErrorRegion key={location.pathname} name={location.pathname}>
						{children}
					</ErrorRegion>
				</div>
			</div>
		</PageSceneActionsContext.Provider>
	);
};
